<template>
  <section
    :class="{
      'homepage-hero relative': true,
    }"
  >
    <VideoBackground v-bind="videoBackground">
      <!-- Black overlay to darken the video background and make text more readable -->
      <div
        :class="{ 'absolute inset-0 left-0 top-0 z-[1] h-full w-full bg-blend-overlay': true }"
        :style="{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }"
      ></div>

      <!-- Text and Content  -->
      <div
        :class="{
          'homepage-hero-container fluid-container relative z-[2] px-5 pb-20 pt-24 md:max-w-[896px] lg:px-10': true,
        }"
        test-id="HeroSectionWithVideo"
      >
        <PersonalizedSectionBuilder
          v-if="closableEventBannerPersonalizedSectionBuilder"
          :data="closableEventBannerPersonalizedSectionBuilder"
          component-type="header-event-banner"
        />
        <PortableText
          v-if="content?.homePagePortableText"
          :portable-data="content.homePagePortableText"
          :class="{
            'homepage-hero-content mx-auto flex w-full flex-col items-center pt-60 text-center md:pt-40 lg:pt-24': true,
          }"
        />
      </div>
    </VideoBackground>
  </section>
</template>

<script setup lang="ts">
import PersonalizedSectionBuilder from '~/components/Atmos/PersonalizedSectionBuilder/PersonalizedSectionBuilder.vue';
import PortableText from '~/components/Atmos/PortableText/PortableText.vue';
import VideoBackground from '~/components/Atmos/VideoBackground/VideoBackground.vue';
import { useRefsStore } from '~/composables/refsStore';
import type {
  HomePage,
  HomepageHero,
  VideoBackground as VideoBackgroundData,
} from '~/sanity/types';

const props = defineProps<HomepageHero & Pick<HomePage, 'eventBanner'>>();
const { getResolvedPersonalizedSectionBuilder } = useRefsStore();

// the wrapper still provides the hero layout and text color when no video is configured
const videoBackground = computed<VideoBackgroundData>(
  () => props.heroBackground ?? { _type: 'videoBackground' }
);

// Convert overlay percentage string (e.g. '80') to decimal opacity (e.g. 0.8)
const overlayOpacity = computed(() => {
  const overlayValue = videoBackground.value.overlay;
  if (!overlayValue) {
    return 0;
  }
  return parseInt(overlayValue, 10) / 100;
});

const closableEventBannerPersonalizedSectionBuilder = await getResolvedPersonalizedSectionBuilder(
  props?.eventBanner?.reference?._ref
);
</script>

<style lang="scss" scoped>
:deep(.homepage-hero-content) {
  @media (min-width: 1700px) {
    @apply max-w-1600px px-0;
  }
  @media (min-width: 1024px) and (max-width: 1699px) {
    @apply max-w-full;
  }
  .container {
    @apply max-w-full;
  }
  h1 span {
    @apply mb-3 text-[88px] font-black leading-[94.16px] tracking-[-2.2px];
    @apply max-lg:mx-auto max-lg:max-w-[782px] max-lg:text-[79px] max-lg:leading-[89px];
    @apply max-md:max-w-[456px] max-md:text-[48px] max-md:leading-[58px];
  }
  p.content {
    @apply mb-11 lg:mb-13px !important;
    span {
      @apply mx-auto max-w-[506px] text-[18.4px] font-semibold leading-[29.9px] text-www-color-white md:max-w-[672px];
    }
  }
  .button-wrapper {
    @apply gap-4 max-md:max-w-[437px] !important;
  }
}
</style>
